import {
  ClearOutlined,
  DisconnectOutlined,
  HomeOutlined,
  QrcodeOutlined,
  ReloadOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { Collapse, Empty, Modal } from 'antd';
import { updateTerminal } from 'apis';
import { configureTerminal } from 'apis/configureTerminal';
import { releaseTerminal } from 'apis/releaseTerminal';
import { restartApp } from 'apis/restartApplication';
import { luhn } from 'cdigit';
import {
  Box,
  Button,
  CopyButton,
  FormGroup,
  Icon,
  Text,
  VibrantIcon,
} from 'components';
import { FormGroupItem } from 'components/FormGroupItem';
import * as Styles from 'components/FormGroupItem/FormGroupItem.styles';
import {
  ChangeTerminalMode,
  CreateQRLogin,
  TerminalSettingsModal,
} from 'features/merchants';
import { maxNumberOfTerminalsSelector } from 'features/merchants/redux';
import { FirebaseCollectionsType } from 'firebaseAPI';
import React, { Fragment, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFirestore, useFirestoreConnect } from 'react-redux-firebase';
import { styled } from 'theme';
import type { Firebase } from 'types/firebase';
import { formatTime } from 'utils';
import { AddTerminalModal } from './AddTerminalModal';
import { Container } from './MerchantTerminalDetails.styles';

const StyledEmpty = styled(Empty)`
  && {
    margin: 0;
  }
`;

export type MerchantTerminalDetailsProps = {
  merchantId: string;
  accountId: string;
  terminals: Firebase.Terminal[];
  onSave: (terminalId: string, value: string) => Promise<void>;
};

const MerchantTerminalDetails: React.FC<MerchantTerminalDetailsProps> = (
  props,
) => {
  const firestore = useFirestore();
  const [isForcingValidationOnIMEI, setIsForcingValidationOnIMEI] = useState(
    false,
  );
  const [showModal, setShowModal] = React.useState(false);
  const [
    showTerminalSettingsModal,
    setShowTerminalSettingsModal,
  ] = React.useState(false);

  useFirestoreConnect({
    collection: FirebaseCollectionsType.VIBRANT_API_ACCOUNTS,
    storeAs: `account_${props?.accountId}`,
    doc: props?.accountId,
  });

  const terminalsQuery = React.useMemo(() => {
    if (props?.accountId) {
      return {
        collection: FirebaseCollectionsType.TERMINALS,
        storeAs: `terminals_${props?.accountId}`,
        doc: props?.accountId,
        subcollections: [
          {
            collection: FirebaseCollectionsType.TERMINAL_LIST,
            storeAs: FirebaseCollectionsType.TERMINAL_LIST,
          },
        ],
      };
    } else {
      return [];
    }
  }, [props?.accountId]);
  useFirestoreConnect(terminalsQuery);

  const maxNumberOfTerminals = useSelector(
    maxNumberOfTerminalsSelector(props.accountId),
  );

  const labels = (terminal: Firebase.Terminal) => [
    {
      label: 'Merchant Id',
      value: props.merchantId,
      copy: true,
    },
    {
      label: 'Terminal Id',
      value: terminal.terminalId,
      copy: true,
    },
    {
      label: 'Device Id',
      value: terminal?.uid ?? 'No device',
      copy: true,
    },
    {
      label: 'App Id',
      value: terminal?.softpayConfig?.appId ?? 'N/A',
      copy: true,
    },
    // {
    //   label: 'Mode',
    //   value: terminal?.mode?.toUpperCase() ?? 'N/A',
    // },
    {
      label: 'Created At',
      value: terminal.createdAt ?? 'N/A',
    },
  ];

  const createLabel = (label: {
    label: string;
    value: string | number;
    copy?: boolean;
  }) => (
    <Styles.Container>
      <Styles.LabelContainer>
        <Text size="ui3">{label.label}</Text>
        <Styles.ValueText size="ui2">
          {typeof label.value === 'number'
            ? formatTime(label.value)
            : label.value}
        </Styles.ValueText>
      </Styles.LabelContainer>
      {label.copy && <CopyButton text={label.value.toString()} />}
    </Styles.Container>
  );
  const handleUpdatingTerminalIMEI = useCallback(
    () => setIsForcingValidationOnIMEI(true),
    [],
  );

  const alertDescription = useMemo(
    () => (
      <Fragment>
        It seems the IMEI is not correct,
        <span
          onClick={handleUpdatingTerminalIMEI}
          style={{ cursor: 'pointer', textDecoration: 'underline' }}
        >
          click here if you are sure that the IMEI is correct
        </span>
      </Fragment>
    ),
    [handleUpdatingTerminalIMEI],
  );

  const isOnlyDigits = (myString: string) => /\d/.test(myString);

  const validateIMEI = useCallback(
    (IMEI: string | number) => {
      const newIMEI = IMEI.toString();

      if (newIMEI.length < 3 || !isOnlyDigits) {
        setIsForcingValidationOnIMEI(false);
        return true;
      }

      if (newIMEI.length === 0) {
        setIsForcingValidationOnIMEI(false);
      }

      // The IMEI (15 decimal digits: 14 digits plus a check digit)
      // which is why we're using luhn/10mod algorithm as the check digit validator for IMEI.
      if (
        (newIMEI.length === 15 && luhn.validate(newIMEI)) ||
        isForcingValidationOnIMEI
      ) {
        return true;
      }

      return false;
    },
    [isForcingValidationOnIMEI],
  );

  const dispatch = useDispatch();

  const _showTerminalModal = () => {
    alert('Currently not implemented');
    //dispatch(showAddTerminalModal({}));
  };

  const updateTerminalIMEI = useCallback(
    (terminalId: string): ((text: string | number) => void) | undefined => (
      newIMEI,
    ) => {
      const IMEI = newIMEI.toString();

      if (validateIMEI(IMEI)) {
        props.onSave(terminalId, IMEI);
      }

      setIsForcingValidationOnIMEI(false);
    },
    [props, validateIMEI],
  );

  const updateTerminalName = useCallback(
    (
      terminalId: string,
    ): ((text: string | number) => void) | undefined => async (
      newTerminalName,
    ) => {
      await updateTerminal(
        { name: String(newTerminalName) },
        props.merchantId,
        terminalId,
      );
    },
    [props],
  );
  const updateTerminalMode = useCallback(
    (
      terminalId: string,
    ): ((text: string | number) => void) | undefined => async (
      newTerminalName,
    ) => {
      await updateTerminal(
        { name: String(newTerminalName) },
        props.merchantId,
        terminalId,
      );
    },
    [props],
  );

  const updateActivationCode = useCallback(
    (
      terminalId: string,
    ): ((text: string | number) => void) | undefined => async (
      newActivationCode,
    ) => {
      await updateTerminal(
        { activationCode: String(newActivationCode) },
        props.merchantId,
        terminalId,
      );
    },
    [props],
  );
  const _releaseTerminal = async (merchantId: string, terminalId: string) => {
    console.log('Release merchantId', merchantId);
    console.log('Release terminalId', terminalId);
    if (confirm('Release this terminal? This cannot be undone.') == true) {
      console.log('I am releasing now');
      await releaseTerminal(merchantId, terminalId);
    } else {
      console.log('lets not release');
    }
  };
  const _restartApp = async (terminalId: string, accountId: string) => {
    console.log('Restart terminalId ', terminalId);
    console.log('Restart accountId', accountId);
    if (confirm('Restart the app on this terminal?') == true) {
      console.log('I am restarting app now');
      await restartApp(terminalId, accountId);
    } else {
      console.log('lets not restart app');
    }
  };
  const _configureTerminal = async (terminalId: string, accountId: string) => {
    console.log('Configure terminalId', terminalId);
    console.log('Configure accountId', accountId);
    if (confirm('Force configure this terminal?') == true) {
      console.log('I am configuring terminal now');
      await configureTerminal(terminalId, accountId);
    } else {
      console.log('lets not configure terminal');
    }
  };
  const _resetTerminalAction = async (
    terminalId: string,
    accountId: string,
  ) => {
    if (confirm('Reset the action on this terminal?') == true) {
      await firestore
        .collection(FirebaseCollectionsType.TERMINALS)
        .doc(accountId)
        .collection(FirebaseCollectionsType.TERMINAL_LIST)
        .doc(terminalId)
        .update({
          action: null,
        });
    }
  };

  return (
    <Container>
      {props.accountId && (
        <Modal
          visible={showTerminalSettingsModal}
          onCancel={() => setShowTerminalSettingsModal(false)}
          footer={null}
          closeIcon={<VibrantIcon icon="close" size={20} />}
          style={{ maxWidth: 400, width: '100%' }}
        >
          <TerminalSettingsModal accountId={props.accountId} />
        </Modal>
      )}
      <FormGroup
        icon={<HomeOutlined />}
        title={`Terminals (max ${maxNumberOfTerminals})`}
        onCreate={() => _showTerminalModal()}
        onUpdate={() => setShowTerminalSettingsModal(true)}
      >
        <AddTerminalModal />
        {props.terminals?.length > 0 ? (
          <Collapse>
            {props.terminals?.map((terminal) => (
              <Collapse.Panel
                key={terminal.terminalId}
                header={`${terminal.name}: ${terminal.terminalId}`}
              >
                {labels(terminal).map((label) => (
                  <Fragment key={label.label}>{createLabel(label)}</Fragment>
                ))}
                {/* @ts-ignore */}
                <ChangeTerminalMode
                  terminal={terminal}
                  merchantId={props.merchantId}
                  accountId={props.accountId}
                />
                <FormGroupItem
                  label="Name"
                  name="Name"
                  alternativeLabel="Change Name"
                  value={terminal.name}
                  onSave={updateTerminalName(terminal.terminalId)}
                />
                <FormGroupItem
                  label="Activation Code"
                  name="Activation Code "
                  alternativeLabel="Change Activation Code"
                  value={terminal.activationCode}
                  onSave={updateActivationCode(terminal.terminalId)}
                />
                <FormGroupItem
                  label="IMEI"
                  name="IMEI"
                  isValid={validateIMEI}
                  alertDescription={alertDescription}
                  alternativeLabel="Change IMEI"
                  value={terminal.imei ?? ''}
                  onSave={updateTerminalIMEI(terminal.terminalId)}
                />
                <Box mb="12px">
                  <Text size="ui3">Terminal Actions</Text>
                </Box>
                <Box flexDirection="column" display="flex">
                  {terminal.softpayConfig && (
                    <Box mb="8px">
                      <Button
                        onClick={() =>
                          _releaseTerminal(
                            props.merchantId,
                            terminal.terminalId,
                          )
                        }
                        type="primary"
                      >
                        <Box
                          display="flex"
                          alignItems="center"
                          minWidth="220px"
                        >
                          <Box mr="8px">
                            {/* @ts-ignore */}
                            <Icon
                              icon={<DisconnectOutlined />}
                              color="White000"
                            />
                          </Box>
                          <Text size="ui2"> Release Terminal</Text>
                        </Box>
                      </Button>
                    </Box>
                  )}
                  {terminal.softpayConfig && (
                    <Box mb="8px">
                      <Button
                        onClick={() =>
                          _restartApp(terminal.terminalId, props.accountId)
                        }
                        type="primary"
                      >
                        <Box
                          display="flex"
                          alignItems="center"
                          minWidth="220px"
                        >
                          <Box mr="8px">
                            {/* @ts-ignore */}
                            <Icon icon={<ReloadOutlined />} color="Brand500" />
                          </Box>
                          <Text size="ui2"> Restart app</Text>
                        </Box>
                      </Button>
                    </Box>
                  )}

                  {terminal.softpayConfig && (
                    <Box mb="8px">
                      <Button
                        onClick={() =>
                          _configureTerminal(
                            terminal.terminalId,
                            props.accountId,
                          )
                        }
                        type="primary"
                      >
                        <Box
                          display="flex"
                          alignItems="center"
                          minWidth="220px"
                        >
                          <Box mr="8px">
                            {/* @ts-ignore */}
                            <Icon icon={<SettingOutlined />} color="Brand500" />
                          </Box>
                          <Text size="ui2"> Configure app</Text>
                        </Box>
                      </Button>
                    </Box>
                  )}

                  <Box mb="8px">
                    <Button onClick={() => setShowModal(true)} type="primary">
                      <Box display="flex" alignItems="center" minWidth="220px">
                        <Box mr="8px">
                          {/* @ts-ignore */}
                          <Icon icon={<QrcodeOutlined />} color="Brand500" />
                        </Box>
                        <Text size="ui2"> Create QR login</Text>
                      </Box>
                    </Button>
                  </Box>

                  <Box mt="16px">
                    <Box mb="8px" maxWidth="320px">
                      <Text size="ui3" color="label">
                        Use with care. This clears a stuck action and should
                        only be used to recover a terminal that is stuck in busy
                        mode.
                      </Text>
                    </Box>
                    <Button
                      onClick={() =>
                        _resetTerminalAction(
                          terminal.terminalId,
                          props.accountId,
                        )
                      }
                      type="primary"
                    >
                      <Box display="flex" alignItems="center" minWidth="220px">
                        <Box mr="8px">
                          {/* @ts-ignore */}
                          <Icon icon={<ClearOutlined />} color="Brand500" />
                        </Box>
                        <Text size="ui2"> Reset Terminal Action</Text>
                      </Box>
                    </Button>
                  </Box>
                </Box>
                <Modal
                  visible={showModal}
                  onCancel={() => setShowModal(false)}
                  footer={null}
                  closeIcon={<VibrantIcon icon="close" size={20} />}
                  style={{ maxWidth: 500, width: '100%' }}
                >
                  <CreateQRLogin terminal={terminal} />
                </Modal>
              </Collapse.Panel>
            ))}
          </Collapse>
        ) : (
          <StyledEmpty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No terminals"
          />
        )}
      </FormGroup>
    </Container>
  );
};

export { MerchantTerminalDetails };
